import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconAiChatMode } from '@components/icons/ai-chat-mode';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconLoop } from '@components/icons/loop';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AIBuilderMode } from 'api/ai-builder/requests';

type ChatHeaderProps = {
  mode: AIBuilderMode;
  onClose?: () => void;
  onClearChat: () => void;
  isGenerating: boolean;
  journeyShortId: string;
};

export const ChatHeader = ({ mode, onClose, onClearChat, isGenerating }: ChatHeaderProps) => {
  const { t, styledTranslate } = useScopedTranslate(locale);

  const modeLabel = mode === 'agent' ? t('agentMode') : t('planMode');
  const navigatorText = styledTranslate('navigator', {
    navigator: { text: t('navigatorLabel'), color: 'textPrimary', font: 'bold18' },
    mode: { text: modeLabel, color: 'textPrimary', font: 'bold18' },
  });

  const ModeIcon = mode === 'agent' ? IconAiAgent : IconAiChatMode;

  return (
    <StyledHeader>
      <Stack space={2} orientation="horizontal" align="center">
        <ModeIcon variant="gradient" />
        <Text font="bold18" color="textBullet" text={navigatorText} />
      </Stack>
      <Stack space={4} orientation="horizontal" align="center">
        <Menu
          buttonProps={{
            variant: 'ghost',
            size: 'sm',
            iconLeft: <IconMoreVertical />,
          }}
        >
          <Menu.Item
            icon={<IconLoop color="red100" />}
            label={t('clearChat')}
            variant="danger"
            onSelect={onClearChat}
            isDisabled={isGenerating}
          />
        </Menu>
        <IconButton
          icon={<IconCloseRegular />}
          onClick={onClose}
          variant="ghost-purple"
          size="sm"
        />
      </Stack>
    </StyledHeader>
  );
};

const StyledHeader = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
})({
  borderBottom: `1px solid ${theme.color.border10}`,
  padding: pxToRem(16),
});

const locale = {
  en: {
    navigator: '{navigator} • {mode}',
    navigatorLabel: 'Navigator',
    planMode: 'Plan',
    agentMode: 'Agent',
    clearChat: 'Clear chat',
  },
};
